import { useState, useEffect } from "react";
import { Organization } from "@/types/models";
import { getUserOrgs } from "@/services/users";
import { useOrganization } from "@/context/OrganizationContext";
import useUser from "@/hooks/use-user";
import { useRouter } from "next/navigation";
import { deleteOrganization as deleteOrgService } from "@/services/organizations";

const useUserOrganizations = () => {
  const { user } = useUser();
  const { setOrganization, updateOrganizationName } = useOrganization();
  const [userOrganizations, setUserOrganizations] = useState<Organization[]>([]);

  const [selectedOrganization, setSelectedOrganization] = useState<string | null>(null);
  const router = useRouter();

  useEffect(() => {
    const getUserOrganizations = async () => {
      if (user) {
        const orgs: Organization[] = await getUserOrgs();
        const adminOrgs = orgs.filter(
          (org) => org.role === "admin" || org.role === "owner"
        );
        setUserOrganizations(adminOrgs);

        const storedOrgId = localStorage.getItem("selectedOrganizationId");
        const isValidStoredOrgId = adminOrgs.some(
          (org) => org.organization_id === storedOrgId
        );
        const defaultOrgId = adminOrgs[0]?.organization_id;
        const orgIdToSet = isValidStoredOrgId ? storedOrgId : defaultOrgId;

        setSelectedOrganization(orgIdToSet);
        setOrganization(orgIdToSet);
      }
    };
    getUserOrganizations();
  }, [user, setOrganization]);

  const handleSelectChange = (organizationId: string) => {
    setSelectedOrganization(organizationId);
    setOrganization(organizationId);
    localStorage.setItem("selectedOrganizationId", organizationId);
    router.push("/batches");
  };

  const defineSelectData = () => {
    return userOrganizations?.map((org) => ({
      value: org.organization_id,
      label: org.name,
    }));
  };

  const addAndSelectOrganization = (newOrganization: Organization) => {
    setUserOrganizations((prevOrgs) => [...prevOrgs, newOrganization]);
    handleSelectChange(newOrganization.organization_id);
  };

  const deleteOrganization = async (organizationId: string) => {
    await deleteOrgService(organizationId);
    setUserOrganizations((prevOrgs) => prevOrgs.filter(org => org.organization_id !== organizationId));
    const remainingOrgs = userOrganizations.filter(org => org.organization_id !== organizationId);
    const newSelectedOrgId = remainingOrgs[0]?.organization_id || null;
    setSelectedOrganization(newSelectedOrgId);
    setOrganization(newSelectedOrgId);
    localStorage.setItem("selectedOrganizationId", newSelectedOrgId || '');
  };

  const updateSelectedOrganizationName = (newName: string) => {
    if (selectedOrganization) {
      updateOrganizationName(selectedOrganization, newName);
    }
  };

  const userIsOwner = (organizationId: string | null) => {
    const org = userOrganizations.find(org => org.organization_id === organizationId);
    return org?.role === "owner";
  };

  const getSelectedOrganizationCanCreatePublic = () => {
    const org = userOrganizations.find(
      (org) => org.organization_id === selectedOrganization
    );
    return org?.can_create_public ?? false;
  };

  return {
    userOrganizations,
    selectedOrganization,
    handleSelectChange,
    defineSelectData,
    addAndSelectOrganization,
    deleteOrganization,
    userIsOwner,
    updateSelectedOrganizationName,
    getSelectedOrganizationCanCreatePublic,
  };
};

export default useUserOrganizations;